<template>
  <div class="container mt-4">

    <!-- TOP BAR -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h4 class="mb-0">
       
        {{ $t('certificateReview.title') }}
      </h4>

      <!-- Admin only -->
     <button
  v-if="isAdmin"
  class="btn btn-sm text-white"
  style="background:#b03a45;"
  @click="goToMyCertificates"
>
  📄 {{ $t('certificateReview.myCertificates') }}  
</button>

    </div>

    <div class="card shadow border-0">
      <div
        class="card-header text-white fw-bold"
        style="background: linear-gradient(195deg, #4b5563, #8d2e38);"
      >
        <i class="material-icons me-2">request_quote</i>
        {{ $t('certificateReview.header') }}
      </div>

      <div class="table-responsive">
        <table class="table align-middle mb-0">
          <thead class="text-uppercase small bg-light">
            <tr>
              <th>{{ $t('certificateReview.table.employee') }}</th>
              <th>{{ $t('certificateReview.table.type') }}</th>
              <th>{{ $t('certificateReview.table.status') }}</th>
              <th>{{ $t('certificateReview.table.remarks') }}</th>
              <th>{{ $t('certificateReview.table.actions') }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="req in requests" :key="req.id">
              <td>{{ req.user?.firstname }} {{ req.user?.lastname }}</td>

              <td>
                <span v-if="req.certificate_type?.name">{{ req.certificate_type.name }}</span>
                <span v-else-if="req.certificateType?.name">{{ req.certificateType.name }}</span>
                <span v-else class="text-muted">{{ $t('certificateReview.noType') }}</span>
              </td>

              <td>
                <span :class="['badge', statusClass(req.status)]">
                  {{ req.status.toUpperCase() }}
                </span>
              </td>

              <td>{{ req.remarks || '-' }}</td>

              <td>
                <template v-if="req.status === 'pending'">
                  <button class="btn btn-success btn-sm me-2" @click="markReady(req.id)">
                    ✔ {{ $t('certificateReview.actions.accept') }}
                  </button>
                  <button class="btn btn-danger btn-sm me-2" @click="openRejectModal(req.id)">
                    ✘ {{ $t('certificateReview.actions.reject') }}
                  </button>
                </template>

                <button
                  v-if="req.status === 'ready'"
                  class="btn btn-primary btn-sm"
                  @click="generatePDF(req.id)"
                >
                  📄 {{ $t('certificateReview.actions.pdf') }}
                </button>
              </td>
            </tr>

            <tr v-if="requests.length === 0">
              <td colspan="5" class="text-center text-muted py-4">
                {{ $t('certificateReview.noRequests') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Rejection Modal -->
    <div class="modal fade" id="rejectModal" tabindex="-1" aria-labelledby="rejectModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header bg-danger text-white">
            <h5 class="modal-title" id="rejectModalLabel">
              <i class="material-icons me-2">cancel</i>
              {{ $t('certificateReview.rejectModal.title') }}
            </h5>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>

          <div class="modal-body">
            <div class="mb-3">
              <label for="rejectRemarks" class="form-label">
                {{ $t('certificateReview.rejectModal.label') }}
                <span class="text-danger">*</span>
              </label>
              <textarea
                v-model="rejectionRemarks"
                class="form-control"
                id="rejectRemarks"
                rows="3"
                :placeholder="$t('certificateReview.rejectModal.placeholder')"
                required
              ></textarea>
              <div class="form-text text-danger" v-if="showRemarkError">
                {{ $t('certificateReview.rejectModal.error') }}
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              {{ $t('certificateReview.rejectModal.cancel') }}
            </button>
            <button type="button" class="btn btn-danger" @click="submitRejection">
              <i class="material-icons me-1">cancel</i>
              {{ $t('certificateReview.rejectModal.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api'
import { useToast } from 'vue-toastification'
import { Modal } from 'bootstrap'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

const requests = ref([])
const currentRequestId = ref(null)
const rejectionRemarks = ref('')
const showRemarkError = ref(false)
let rejectModal = null


const user = JSON.parse(localStorage.getItem('user') || '{}')


const rolesNormalized = computed(() => {
  const r = user?.roles

  if (Array.isArray(r) && typeof r[0] === 'string') {
    return r.map(x => String(x).toLowerCase())
  }
  if (Array.isArray(r) && typeof r[0] === 'object') {
    return r.map(x => String(x?.name || x?.role || '').toLowerCase()).filter(Boolean)
  }
  if (typeof user?.role === 'string') return [user.role.toLowerCase()]

  return []
})

const isAdmin = computed(() => {
  return (
    rolesNormalized.value.includes('admin') ||
    rolesNormalized.value.includes('SuperAdmin') ||
    user?.is_admin === true ||
    user?.isAdmin === true
  )
})


const goToMyCertificates = () => {
  router.push('/employee/certificate-requests/request')
}


const fetchRequests = async () => {
  try {
    const res = await axios.get('/certificate-requests/all')
    requests.value = res.data.data.data || []
  } catch (err) {
    toast.error(t('certificateReview.messages.loadError'))
  }
}


const markReady = async (id) => {
  if (!confirm(t('certificateReview.messages.confirmAccept'))) return

  try {
    await axios.patch(`/certificate-requests/${id}/ready`)
    toast.success(t('certificateReview.messages.acceptSuccess'))
    fetchRequests()
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Action failed'
    toast.error(`❌ ${errorMessage}`)
  }
}


const openRejectModal = (id) => {
  currentRequestId.value = id
  rejectionRemarks.value = ''
  showRemarkError.value = false
  if (!rejectModal) rejectModal = new Modal(document.getElementById('rejectModal'))
  rejectModal.show()
}


const submitRejection = async () => {
  if (!rejectionRemarks.value.trim()) {
    showRemarkError.value = true
    return
  }

  try {
    await axios.patch(`/certificate-requests/${currentRequestId.value}/reject`, {
      remarks: rejectionRemarks.value
    })
    rejectModal.hide()
    toast.success(t('certificateReview.messages.rejectSuccess'))
    fetchRequests()
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Rejection failed'
    toast.error(`❌ ${errorMessage}`)
  }
}


const generatePDF = async (requestId) => {
  const loadingToast = toast.info(
    t('certificateReview.messages.pdfLoading'),
    { timeout: false }
  )

  try {
    const response = await axios.get(
      `/certificate-requests/${requestId}/generate`, 
      {
        responseType: 'blob' 
      }
    )

    const blob = new Blob([response.data], {
      type: 'application/pdf'
    })

    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')

    toast.dismiss(loadingToast)
    toast.success(t('certificateReview.messages.pdfSuccess'))
  } catch (err) {
    console.error('PDF error:', err)
    toast.dismiss(loadingToast)
    toast.error(t('certificateReview.messages.pdfError'))
  }
}



const statusClass = (status) => {
  switch (status) {
    case 'ready': return 'bg-success text-white'
    case 'rejected': return 'bg-danger text-white'
    default: return 'bg-secondary text-white'
  }
}

onMounted(() => {
  fetchRequests()
  rejectModal = new Modal(document.getElementById('rejectModal'))
})
</script>

<style scoped>
.badge {
  padding: 0.4em 0.8em;
  border-radius: 0.5rem;
  text-transform: uppercase;
  font-size: 0.75rem;
}
</style>
